// Microbe RTS — THE TUNING SURFACE.
//
// Every balance number in the game lives here. The sim/render/ui read these; none of them
// hardcode a number that belongs in a data table. This is the file Ara edits to tune the
// recipe through playtesting. Values below are a common-sense STARTING STATE straight from
// the design bible (Synthesis v1) — NOT a balance pass. Tinker freely.
//
// Numbers are grouped by system. Anything the bible authored "to live in a data table" is here.

import type { WarriorClass, NodeType, BandStance, UnitKind } from './types.js';

// ============================================================================
// WORLD & MAP  (tall vertical open-water map; depth IS the economy)
// ============================================================================
export const WORLD = {
  width: 8400, // ~2x wider than the original 4200 — more room to maneuver + hide in the fog
  height: 9200,
  baseUnitSpeed: 95, // world u/s — full-height traverse ~95s
  // depth bands (normalized d): [min,max]
  bands: {
    photic: [0.0, 0.2], // full light, lean nutrients, no toxin
    meso: [0.2, 0.55], // fading light, moderate nutrients, homes live here
    deep: [0.55, 0.85], // dark, rich nutrients, moderate toxin
    abyssal: [0.85, 1.0], // dark, richest nutrients, high toxin
  },
} as const;

/** Sunlight field: full near top, zero at d>=0.60. SUN(d)=clamp((0.60-d)/0.50,0,1). */
export function SUN(depth: number): number {
  return Math.max(0, Math.min(1, (0.6 - depth) / 0.5));
}
/** Ambient toxin DPS by depth: 0 above d=0.55, up to 6 at the floor. */
export function TOXIN_DPS(depth: number): number {
  return 6 * Math.max(0, Math.min(1, (depth - 0.55) / 0.45));
}

// ============================================================================
// NODES  (2 Homes + 11 capturable in Standard)
// yields are PER SLOT PER SECOND (workerCap = auto-work slots): an owned node auto-produces at
// energyPerWorker × workerCap × ECON.autoWorkEfficiency, no units needed. Names kept for continuity.
// ============================================================================
export interface NodeSpec {
  type: NodeType;
  energyPerWorker: number;
  aminoPerWorker: number;
  toxinPerWorker: number;
  integrity: number;
  workerCap: number;
  radius: number;
  needsPhoto?: boolean; // Light Shelf: worthless until Photosynthesis
  requiresResist?: boolean; // Toxin Seep mining wants resistance
}
export const NODE_SPECS: Record<NodeType, NodeSpec> = {
  home: { type: 'home', energyPerWorker: 0, aminoPerWorker: 0, toxinPerWorker: 0, integrity: 1500, workerCap: 0, radius: 130 },
  reef: { type: 'reef', energyPerWorker: 0.6, aminoPerWorker: 0.25, toxinPerWorker: 0, integrity: 500, workerCap: 4, radius: 110 },
  vent: { type: 'vent', energyPerWorker: 1.0, aminoPerWorker: 0.35, toxinPerWorker: 0, integrity: 400, workerCap: 5, radius: 110 },
  light: { type: 'light', energyPerWorker: 1.6, aminoPerWorker: 0.05, toxinPerWorker: 0, integrity: 400, workerCap: 6, radius: 110, needsPhoto: true },
  toxin: { type: 'toxin', energyPerWorker: 0.4, aminoPerWorker: 0, toxinPerWorker: 0.5, integrity: 400, workerCap: 3, radius: 100, requiresResist: true },
};

/** Home passive trickle (worker-free), per second. */
export const HOME_TRICKLE = { energy: 0.8, amino: 0.2 };
/** Anti-stall: EVERY owned node trickles this regardless of workers, per second. */
export const NODE_TRICKLE = { energy: 0.5, amino: 0.15 };

/**
 * PROCEDURAL NODE LAYOUT.  Node positions are re-rolled every match from a seeded RNG
 * (sim/world.ts) instead of a fixed table — so no two games play the same map.
 *
 *   • Homes are NOT owned at start: two NEUTRAL home craters spawn, one on each side of the map.
 *     Each colony must send its dropped squad to FIND and colonize a home before it can build.
 *   • Every capturable node is placed randomly within its type's depth band, across the full
 *     width, rejecting any spot too close (minSepFrac * height) to an already-placed node.
 *   • Photosynthetic light shelves still cluster near the surface; there are still exactly 3.
 *
 * `x`/`depth` are normalized 0..1; band ranges are [min,max]. Counts sum to capturableNodeCount.
 */
export interface NodeGenType { type: NodeType; count: number; depth: [number, number]; }
export const NODE_GEN = {
  minSepFrac: 0.075, // no two nodes closer than this * WORLD.height (~690u at 9200)
  xRange: [0.07, 0.93] as [number, number], // keep nodes off the very edges
  // Homes: one per side, mid-depth. The player's side is the LEFT band, the bot's the RIGHT,
  // so each squad drops on the OPPOSITE side from its rival (spec).
  home: {
    depth: [0.3, 0.46] as [number, number],
    youX: [0.1, 0.26] as [number, number],
    botX: [0.74, 0.9] as [number, number],
    // Drone drop: a RANDOM point ANYWHERE on the map (no more "your third"), reject-sampled to stay
    // well CLEAR of every crater (minDropFromNode) so you always start in open water and must explore
    // to find a crater to settle. The two opening drops are pushed ≥ minSpawnSepFrac of the map
    // DIAGONAL apart so the sides never start on top of each other.
    spawnX: [0.07, 0.93] as [number, number], // drop anywhere across the width...
    spawnDepth: [0.08, 0.92] as [number, number], // ...and anywhere top-to-floor (ambient toxin removed)
    minSpawnSepFrac: 0.4, // the two drops sit ≥40% of the map diagonal apart
    minDropFromNode: 750, // world units of clearance from any node at the drop point
    dropSpread: 0, // single drone — no squad jitter needed
    // (legacy per-side drop bands — no longer used now that spawns are map-wide + separation-gated)
    dropDepth: [0.12, 0.6] as [number, number],
    youDropX: [0.04, 0.3] as [number, number],
    botDropX: [0.7, 0.96] as [number, number],
  },
  capturable: [
    { type: 'light', count: 3, depth: [0.05, 0.18] }, // photic light shelves (near surface)
    { type: 'reef', count: 4, depth: [0.24, 0.52] }, // meso reef outcrops
    { type: 'vent', count: 2, depth: [0.58, 0.76] }, // deep nutrient vents
    { type: 'toxin', count: 2, depth: [0.82, 0.95] }, // abyssal toxin seeps
  ] as NodeGenType[],
};

export const CAPTURE = {
  // A base is a COMBAT STRUCTURE, not a flag. You don't stand on a node to flip it — a
  // WARRIOR must beat the core down (integrity -> 0) to destroy it, and only a DRONE (the
  // seeder — she becomes a Queen on her first successful seed) can then found/rebuild a colony
  // on the crater. The big `node.radius` ring stays purely as the ooze-damage aura (see
  // BASE_DEFENSE); combat/establish key off the small core hitbox.
  coreRadiusFrac: 0.45, // core combat hitbox = node.radius * this (the "structure")
  establishSeconds: 6, // drone channel on a neutral crater to seed (found/rebuild) a colony
  founderRepairPerSec: 8, // integrity/sec healed per friendly drone/queen in the ring (capped)
  maxRepairFounders: 3, // repair contribution caps here (out-DPS it with enough warriors)
  broodBuildSeconds: 20, // establish window for a new forward Brood
};

// The cost to SEED a new colony (channel a drone on a neutral crater). Your FIRST colony —
// founding your Home while you still have none — is FREE; every seed after that (forward
// colonies, or a re-seed of a lost base) is charged to your colony when the channel completes.
// Deliberate early-game friction so you can't spam bases off a single free drone.
export const SEED_COST = { energy: 150, amino: 70, toxin: 0 } as const;

// ============================================================================
// THE QUEEN  (a drone, promoted the instant she completes her first seed)
// She is to the pentagon what a chess queen is to a bishop: ~+50% across her base stats,
// visibly larger, a touch SLOWER than a raw drone, and self-mends a mild trickle of hp.
// ============================================================================
export const QUEEN = {
  hpMult: 3.0, // a drone's frame with a queen's mass — enough to trade blows, not just drone+50%
  dmgMult: 4.0, // a real bite: she can chew through a lone Grazer (460hp) and win
  armor: 8, // NEW: flat armor (drones have none) — blunts the Grazer's heavy 17-dmg hits
  speedMult: 0.9, // she trades a little speed for all that mass (still faster than a lancer)
  radiusMult: 1.4, // visibly bigger model (chess-queen silhouette in render)
  regenPerSec: 0.01, // self-heal 1% of max hp per second
  // Result (tuned headless): a lone Queen solos ONE Grazer, ending ~⅓–½ hp. Beats 2–3 lancers,
  // dies to 4 — powerful hero unit, not unkillable.
} as const;

// ============================================================================
// ECONOMY  (Energy + scarce Amino; storage scales with territory)
// ============================================================================
export const ECON = {
  // Workers are gone: an owned node is AUTO-WORKED. Its yield = its per-slot rate × workerCap ×
  // this efficiency (so a colony pays passively the moment you own it — territory IS the economy).
  autoWorkEfficiency: 0.7,
  photoMultBase: 1.0,
  photoMultPerLevel: 0.35, // photoMult = 1 + 0.35*(photoLvl-1)
  // storage caps — lifted to a HIGH floor from Tier 1 (Ara: "no negative pressure here"). The old
  // low bases (300 energy + 150/node) meant a Tier-III colony literally couldn't bank the 1250 energy
  // the Tier-IV speciation costs without ~7 nodes, hard-walling progression. Now they start generous
  // and still scale up with territory.
  energyCapBase: 4000,
  energyCapPerNode: 150,
  aminoCapBase: 1000,
  aminoCapPerNode: 75,
  toxinCapBase: 400,
  toxinCapPerToxinNode: 50,
  // salvage: killing an enemy refunds a fraction of its build cost
  salvageEnergyFrac: 0.4,
  salvageAminoFrac: 0.15,
  corpseLifetime: 8, // seconds a corpse is harvestable
};

// starting resources per side, and the DROP (no home yet — you must go seed one).
export const START = {
  energy: 120,
  amino: 30,
  toxin: 0,
  // You begin as literally ONE drone, dropped at a random point on your side of the map (the two
  // sides are min-separated). Lead her to a home crater and seed it — that first seed is free and
  // promotes her to your Queen.
  squad: { drone: 1 } as Partial<Record<UnitKind, number>>,
};

// ============================================================================
// POPULATION
// ============================================================================
export const POP = {
  base: 12, // from Home
  perForwardNode: 6,
  ceiling: 40, // Standard hard cap
};
/** Pop weight per unit kind. (Wild grazers belong to no colony → weight 0.) */
export const POP_WEIGHT: Record<string, number> = {
  lancer: 1, stinger: 1, drone: 1, healer: 1, phalanx: 2, engulfer: 2, toxifer: 2, grazer: 0,
};

// ============================================================================
// UNITS  (costs + combat stats). The 5-class pentagon + the fast drone (your seeder / future Queen).
// ============================================================================
export interface UnitSpec {
  hp: number;
  armor: number;
  dmg: number; // per hit (DoT for toxifer handled specially)
  interval: number; // seconds between attacks
  range: number; // 0 = melee (uses REACH); >0 = ranged
  speed: number; // world u/s
  radius: number;
  cost: { energy: number; amino: number; toxin: number };
  buildSeconds: number;
  pop: number;
}
export const UNITS: Record<WarriorClass, UnitSpec> = {
  lancer: { hp: 60, armor: 0, dmg: 14, interval: 0.7, range: 0, speed: 130, radius: 13, cost: { energy: 40, amino: 15, toxin: 0 }, buildSeconds: 8, pop: 1 },
  phalanx: { hp: 150, armor: 6, dmg: 12, interval: 1.0, range: 0, speed: 55, radius: 18, cost: { energy: 60, amino: 25, toxin: 0 }, buildSeconds: 14, pop: 2 },
  stinger: { hp: 55, armor: 0, dmg: 18, interval: 1.2, range: 200, speed: 85, radius: 13, cost: { energy: 45, amino: 20, toxin: 0 }, buildSeconds: 10, pop: 1 },
  engulfer: { hp: 140, armor: 2, dmg: 26, interval: 1.4, range: 0, speed: 65, radius: 17, cost: { energy: 70, amino: 30, toxin: 0 }, buildSeconds: 14, pop: 2 },
  toxifer: { hp: 75, armor: 0, dmg: 6, interval: 1.5, range: 95, speed: 70, radius: 15, cost: { energy: 60, amino: 25, toxin: 10 }, buildSeconds: 13, pop: 2 },
  // Drone: fastest thing on the map (~ a fully-upgraded lancer, 130*1.15^3 ≈ 197), fragile, light bite.
  // Its real job is vision — it sees far (see VISION) and outruns everything to reveal the fog.
  drone: { hp: 45, armor: 0, dmg: 7, interval: 0.8, range: 0, speed: 178, radius: 12, cost: { energy: 30, amino: 10, toxin: 0 }, buildSeconds: 7, pop: 1 },
};
/** Healer — Tier-III support: quick, fragile, ZERO attack, mends nearby friendly units. */
export const HEALER: UnitSpec = {
  hp: 48, armor: 0, dmg: 0, interval: 1.0, range: 0, speed: 108, radius: 13,
  cost: { energy: 55, amino: 30, toxin: 0 }, buildSeconds: 12, pop: 1,
};
export const HEALER_UNLOCK_TIER = 3; // healer can only be produced once the colony reaches Tier III
export const HEAL = {
  range: 135, // heal any friendly within this
  perSec: 15, // hp/s restored (split is per-target, not shared)
  seekRadius: 460, // when idle, drift toward the nearest hurt friendly within this
};
export const BROOD_COST = { energy: 80, amino: 40, toxin: 0 };

// ============================================================================
// WILD FAUNA  (the neutral 'wild' alliance — Grazers)
// A big, slow, tanky neutral. It won't seek a fight; it only turns hostile when a colony unit
// enters its TERRITORY (a disc around its patrol point) or lands a hit on it. Attacking one
// FARMS amino (per point of damage dealt, plus a lump on the kill). Grazers respawn to keep the
// wild population up, so a defended graze is renewable income.
// ============================================================================
export const GRAZER: UnitSpec = {
  hp: 460, armor: 3, dmg: 17, interval: 1.4, range: 0, speed: 40, radius: 22,
  cost: { energy: 0, amino: 0, toxin: 0 }, buildSeconds: 0, pop: 0,
};
export const WILD = {
  count: 5, // grazers kept alive on the map (respawns top it back up)
  depth: [0.28, 0.86] as [number, number], // they roam the meso→deep bands
  xRange: [0.2, 0.8] as [number, number], // ...but away from the home bands on the edges
  territoryRadius: 340, // an intruding colony unit inside this wakes the grazer
  leashRadius: 820, // a provoked grazer chases no farther than this from its guard point
  provokeSeconds: 6, // stays aggressive this long after its last hit / last intruder sighting
  aminoPerDamage: 0.42, // amino credited to the attacker per point of damage dealt to a grazer
  aminoOnKill: 45, // bonus amino lump when a grazer is slain
  respawnSeconds: 38, // delay before the wild population tops back up after a death
  wanderRadius: 160, // idle drift radius around the guard point
};

// ============================================================================
// COMBAT  (the fully-connected RPS-5 pentagon)
// perHit = max(1, dmg * counterMult - target.armor), then * (1 + flank)
// ============================================================================
export const COMBAT = {
  reach: 6, // melee range = a.r + b.r + reach
  counterMult: 1.5, // damage when attacker is STRONG vs target (the master knob)
  flankBonus: 0.25, // +25% when surrounded by >=3 across >=120 degrees
  flankMinAttackers: 3,
  poiArmorBonus: 2, // unit inside a claimed POI
  poiDamageReduction: 0.15,
  rangedLightBonus: 20, // +range in lit surface water
  aggroRadius: 220, // auto-engage hostiles within this
  envToxinDps: 8, // UNUSED: ambient toxin unit-DoT was removed from economy.ts (kept if it ever returns)
  toxiferDotPerStack: 6, // dmg/s per stack
  toxiferDotDuration: 3,
  toxiferDotMaxStacks: 3,
  toxiferSplashRadius: 40,
};
// ============================================================================
// BASE DEFENSE  (every owned node defends itself; Home strongest, colonies weaker)
//   Tier 1 (always): a contact poison ooze — touch the node's radius, get poisoned.
//     Fixed dps for a fixed duration, RE-applied every `reapplySeconds` while in contact.
//     Non-stacking until the `envenomAtDose`-th consecutive dose, which escalates to a
//     STACKING "envenomed" state. The count resets only when poison fully lapses.
//   Tier 2 (owner colony.tier >= rangedTier): adds a ranged single-target PHYSICAL shot.
// ============================================================================
export const BASE_DEFENSE = {
  // --- Tier 1: contact poison ooze ---
  auraPadding: 8, // aura reaches node.radius + this (a "touch" band just past the rim)
  poisonDuration: 5, // each application lasts this long (spec: 5s)
  reapplySeconds: 2.5, // while in contact, re-dose on this cadence (spec: 2.5s)
  envenomAtDose: 3, // the 3rd consecutive dose flips poison into the stacking state
  poisonMaxStacks: 6, // cap on envenomed stacks
  homePoisonDps: 9, // Home ooze: dps PER STACK (normal poison = 1 stack)
  colonyPoisonDps: 5, // captured-node ooze: noticeably weaker per stack
  // --- Tier 2: ranged single-target physical ---
  rangedTier: 2, // colony tier at which nodes gain the ranged attack
  homeRange: 300, // Home firing range (world units; node radius is 130)
  colonyRange: 220, // captured-node firing range
  homeShotDmg: 24, // Home shot: physical damage (reduced by target armor)
  colonyShotDmg: 14, // captured-node shot damage
  homeShotInterval: 1.5, // seconds between Home shots
  colonyShotInterval: 2.2, // seconds between captured-node shots
  shotTtl: 0.18, // seconds a tracer stays visible
} as const;

// ============================================================================
// HOME TURRETS  (tap-placed defensive structures around your home)
//   Unlocked by a standalone Tier-2 mutation (t2_turret). Cap = min(3, tier-1): 1@T2, 2@T3, 3@T4.
//   Tap-to-place around the home rim, >= minSepDeg apart. Weak ranged shot + a weak poison cloud.
//   Walk-through (no collision); an enemy army in range CHIPS them (proximity dps). Repair = one
//   action restoring all damaged+dead together, cost scaled by missing HP. Starter numbers — tune
//   in the sandbox.
// ============================================================================
export const TURRET = {
  cost: { energy: 100, amino: 55, toxin: 0 }, // per turret (paid on placement)
  maxHp: 80,
  radius: 14,
  buildSeconds: 30, // construction AND repair time (inert while the timer runs)
  range: 230, // ranged-shot reach
  shotDmg: 9, // per shot (reduced by target armor)
  shotInterval: 1.5, // seconds between shots
  auraRadius: 88, // poison cloud radius (world units from turret centre)
  poisonDps: 4, // per-stack poison dps (weaker than a colony node's 5)
  ringMinFrac: 0.75, // placement clamp: min distance from home centre = home.radius * this
  ringMaxFrac: 1.7, // ...and the max (so turrets ring the home, not the open map)
  minSepDeg: 60, // minimum angular separation between a side's turrets (around the home)
} as const;

/** Each class deals counterMult vs the two it is STRONG against. Drone is a pure-stats harasser:
 *  it counters nothing and nothing hard-counters it (its defence is raw speed + vision). */
export const COUNTERS: Record<WarriorClass, WarriorClass[]> = {
  lancer: ['stinger', 'toxifer'],
  phalanx: ['lancer', 'engulfer'],
  stinger: ['phalanx', 'toxifer'],
  toxifer: ['phalanx', 'engulfer'],
  engulfer: ['lancer', 'stinger'],
  drone: [],
};

// ============================================================================
// FOG OF WAR  (presentational vision — the player only sees rival + wild units their own
// units/nodes can currently see; explored terrain stays dimly remembered)
// ============================================================================
export const VISION: { unit: Record<string, number>; node: number; homeNode: number; cell: number; explored: number; hidden: number } = {
  // sight radius (world units) each of YOUR units grants
  unit: {
    lancer: 340, phalanx: 300, stinger: 380, engulfer: 300,
    toxifer: 340, drone: 660, healer: 320,
  },
  node: 430, // an owned capturable node's sight
  homeNode: 540, // an owned home's sight
  cell: 150, // fog grid cell size (world units) — coarse is fine + cheap
  explored: 0.5, // veil opacity over explored-but-currently-unseen terrain
  hidden: 0.88, // veil opacity over never-explored terrain
};

// ============================================================================
// THE THREE CLOCKS
// ============================================================================
export const CLOCKS = {
  // Great Oxygenation sudden-death
  oxyIgniteAt: 35 * 60, // seconds (T_oxy)
  oxyRampSeconds: 8 * 60, // O2 climbs 0->1 over this once active
  oxyBaseDps: 6, // Home erosion = oxyBaseDps * (1 - nodeShare)
  oxyLateRampAt: 42 * 60, // after this, oxyDps grows...
  // ...by this per minute (force a finish). Bumped for the 2x-wider map: even node-share used to
  // let both homes out-heal the base erosion and stalemate past the match cap, so this share-
  // independent ramp now climbs hard enough to guarantee an ending.
  oxyLateRampPerMin: 2.0,
  // Aerobic Respiration keystone flips O2 to fuel:
  oxyBonusPerSec: 4, // +energy/s = oxyBonusPerSec * O2level * nodeShare
  capturableNodeCount: 11, // denominator for nodeShare

  // Bot threat clock (see BOT for difficulty scaling)
  botFirstPushAt: 4.5 * 60,
  botPushInterval: 5 * 60,
  botPushRetry: 20, // if a wave can't commit (army too small) recheck this soon, don't burn the interval
};

// ============================================================================
// WIN CONDITIONS
// ============================================================================
export const WIN = {
  // Domination: hold >= share of capturable nodes; decaying 120s hold, floor 30s.
  dominationShare: 8 / 11, // >= 73%
  dominationHoldMax: 120, // banked hold counts DOWN from here while held
  dominationHoldFloor: 30, // must hold continuously >= this to win
  // Conquest: reduce enemy Home integrity to 0 (handled by node integrity).
};

// ============================================================================
// RIVAL BOT AI  (mirror macro-AI; difficulty scales ONLY the bot)
// ============================================================================
export type Difficulty = 'tidepool' | 'reef' | 'abyss' | 'constrictor';

/** Per-profile expansion personality — how hard/fast a bot lands new colonies. Folded INTO the
 *  profile (was a global in BOT_BEHAVIOR) so a profile is a complete, self-contained bot definition
 *  the sandbox can author and the real game can select by name — same object, both callers. */
export interface BotExpand {
  everySeconds: number;   // cadence between expansion dispatches (lower = grabs land faster)
  desiredScouts: number;  // seeder drones kept on hand for expansion + vision
  scoutRatio: number;     // chance it tops up a seeder while under quota (vs building a warrior)
  parallelSeeds: number;  // idle drones pre-positioned on DISTINCT craters at once (1 = old behavior)
  targetNodes: number;    // hard-BANK income toward SEED_COST until it holds this many capturable
                          // nodes — the anti-turtle lever: it stops frittering amino and pays for land
}

/** WIDENED GENOME (self-play tunables). Each field was formerly a hardcoded constant in sim/bot.ts
 *  or an engine floor (BOT_BEHAVIOR / CLOCKS); exposing it per-profile lets the evolve harness (evolve/)
 *  discover distinct play styles. DEFAULTS (BOT_TUNE_DEFAULTS) equal the former constants exactly, so
 *  existing profiles behave byte-identically until the GA evolves them. See evolve/evolve.ts. */
export interface BotTune {
  minArmyToPush: number;        // warriors needed to launch + min force committed (was BOT_BEHAVIOR 4)
  standingArmyFloor: number;    // army kept while banking for tech (was BOT_BEHAVIOR 8)
  earlyArmyFloor: number;       // army kept while still land-grabbing (was BOT_BEHAVIOR 4)
  botPushInterval: number;      // seconds between successful waves (was CLOCKS.botPushInterval 300)
  specReserveFrac: number;      // how hard to bank toward next speciation gate (was implicit 1.0)
  queenHuntRetreatFrac: number; // Queen disengages a grazer below this HP frac (was literal 0.45)
  huntRadius: number;           // how far the Queen ranges to hunt grazers (was HUNT_RADIUS 3800)
  speciationScore: number;      // evo priority of climbing a tier — rush vs linger (was literal 100)
  aerobicKeystoneScore: number; // evo priority of the oxygen bet (Aerobic keystone) (was literal 55)
  econSomaticScore: number;     // evo priority of economy somatics vs combat (was literal 10)
  classWeights: { lancer: number; stinger: number; phalanx: number; engulfer: number; toxifer: number }; // army-comp bias (was uniform)
}

export interface BotProfile extends BotTune {
  name: string;         // stable id — registry key + sandbox picker label
  incomeMult: number;   // multiplies THIS side's income (per-side via ctx.profiles)
  waveSizeMult: number; // multiplies army it commits per push
  threatClockOffset: number; // seconds (+later / -earlier)
  respectsCounters: boolean; // counter-picks your comp
  focusFires: boolean;
  fogOmniscient: boolean;
  expand: BotExpand;    // expansion personality
}

/** Genome defaults = the former hardcoded constants. Spread into every shipped profile so behavior is
 *  unchanged; the GA overrides specific fields when it breeds a champion (evolve/evolve.ts toProfile). */
export const BOT_TUNE_DEFAULTS: BotTune = {
  minArmyToPush: 4, standingArmyFloor: 8, earlyArmyFloor: 4, botPushInterval: 300,
  specReserveFrac: 1.0, queenHuntRetreatFrac: 0.45, huntRadius: 3800,
  speciationScore: 100, aerobicKeystoneScore: 55, econSomaticScore: 10,
  classWeights: { lancer: 1, stinger: 1, phalanx: 1, engulfer: 1, toxifer: 1 },
};

/** Named, plug-and-play bot profiles. Both the sandbox god-view AND the real game import from this
 *  one registry, so any profile authored/tuned in the harness is instantly selectable in-game. */
export const BOT_PROFILES: Record<string, BotProfile> = {
  // SHIPPED BOT = the EXPANSIONIST land-grabber (proven in the sandbox god-view). Every difficulty now
  // carries the aggressive expand personality — banks hard for seeds, pre-positions drones on distinct
  // craters, and races to own the most nodes fastest. Difficulty scales the COMBAT dials around it
  // (income / wave size / threat-clock / counter-picking / focus-fire / fog), not the land hunger.
  tidepool: {
    ...BOT_TUNE_DEFAULTS,
    // FAIR (no handicap — enforced in createSim). Easiest tier by STRATEGY only: a gentle, passive
    // opening (slow expand, few colonies, no counters/focus, very late first push at +90s).
    name: 'tidepool', incomeMult: 1.0, waveSizeMult: 1.0, threatClockOffset: 90,
    respectsCounters: false, focusFires: false, fogOmniscient: false,
    expand: { everySeconds: 18, desiredScouts: 2, scoutRatio: 0.4, parallelSeeds: 2, targetNodes: 5 },
  },
  reef: {
    // Strategy knobs = the SELF-PLAY EVOLVED champion (evolve/champion.json): beat the PRIOR reef
    // preset 83% in a fair mirror (income/wave/fog pinned equal → won on skill, not handicap). Style:
    // land-grab even harder (expand every 8s vs 14, max parallel seeds) + focus-fire waves. reef's
    // non-strategy dials already equal that fair mirror (1.0/1.0/fog-off), so this is a clean drop-in.
    ...BOT_TUNE_DEFAULTS,
    name: 'reef', incomeMult: 1.0, waveSizeMult: 1.0, threatClockOffset: 40,
    respectsCounters: false, focusFires: true, fogOmniscient: false,
    expand: { everySeconds: 8, desiredScouts: 4, scoutRatio: 0.51, parallelSeeds: 4, targetNodes: 7 },
  },
  abyss: {
    // ABYSS = the FROM-SCRATCH self-play reef-beater (evolve/champion-stinger-swarm.json — 78% vs reef
    // on 16 HELD-OUT unseen seeds, 25W/7L, a trustworthy number: trained on rotating seeds). A stinger-
    // swarm rush-macro: fast-expand (8 nodes), army floor 18, early clock, frequent long-RANGE waves,
    // econ-teching, near-pure Stinger comp.
    // FAIR (no handicap — enforced in createSim). Hardest tier by STRATEGY alone: this stinger-swarm
    // beats reef ~78% on a fair mirror. No income/wave/fog advantage — pure skill.
    ...BOT_TUNE_DEFAULTS,
    name: 'abyss', incomeMult: 1.0, waveSizeMult: 1.0, fogOmniscient: false,
    threatClockOffset: -31, respectsCounters: false, focusFires: false,
    expand: { everySeconds: 6, desiredScouts: 4, scoutRatio: 0.46, parallelSeeds: 4, targetNodes: 8 },
    minArmyToPush: 9, standingArmyFloor: 18, earlyArmyFloor: 0, botPushInterval: 154,
    specReserveFrac: 1.19, huntRadius: 3141, econSomaticScore: 25,
    classWeights: { lancer: 0.02, stinger: 4.83, phalanx: 0.78, engulfer: 0.84, toxifer: 1.22 },
  },
  // CONSTRICTOR — the 20-knob SELF-PLAY champion (evolve/champion.json), shipped as a named style + a
  // 4th difficulty. Fair-footing (income 1.0, no fog) so it wins on STYLE, not handicap: a SLOW-MACRO
  // squeeze — few colonies (4), banks ~86% toward tech, holds a standing army and pushes on a long
  // cadence, fielding an engulfer+toxifer TANK-AND-POISON comp, no focus-fire. Beats the shipped reef
  // 83% in a fair mirror and is more league-robust (fitness 0.81). Only differing-from-default knobs are
  // spelled out; the rest (incl. the pinned queen/spec/aerobic genes) come from BOT_TUNE_DEFAULTS.
  constrictor: {
    ...BOT_TUNE_DEFAULTS,
    name: 'constrictor', incomeMult: 1.0, waveSizeMult: 1.0, fogOmniscient: false,
    threatClockOffset: 53, respectsCounters: false, focusFires: false,
    expand: { everySeconds: 30, desiredScouts: 1, scoutRatio: 0.24, parallelSeeds: 4, targetNodes: 4 },
    minArmyToPush: 5, standingArmyFloor: 9, earlyArmyFloor: 0, botPushInterval: 348,
    specReserveFrac: 0.86, huntRadius: 2613, econSomaticScore: 8,
    classWeights: { lancer: 1.07, stinger: 0.4, phalanx: 1.06, engulfer: 2.4, toxifer: 3.04 },
  },
  // The original standalone land-grab specialist — kept as a named profile so the sandbox still picks it
  // by key (the difficulty presets above are now built on this same expansionist DNA).
  expansionist: {
    ...BOT_TUNE_DEFAULTS,
    name: 'expansionist', incomeMult: 1.0, waveSizeMult: 1.0, threatClockOffset: 40,
    respectsCounters: true, focusFires: false, fogOmniscient: false,
    expand: { everySeconds: 14, desiredScouts: 3, scoutRatio: 0.45, parallelSeeds: 3, targetNodes: 7 },
  },
};

// Difficulty -> profile (real game selects by difficulty; sandbox can select any named profile).
export const BOT: Record<Difficulty, BotProfile> = {
  tidepool: BOT_PROFILES.tidepool,
  reef: BOT_PROFILES.reef,
  abyss: BOT_PROFILES.abyss,
  constrictor: BOT_PROFILES.constrictor,
};
export const DEFAULT_DIFFICULTY: Difficulty = 'reef';

/** Engine-level combat floors shared by every profile (personality lives in BotProfile). */
export const BOT_BEHAVIOR = {
  minArmyToPush: 4, // warriors before it commits a push
  standingArmyFloor: 8, // keep at least this many warriors even while banking for speciation
  // Army kept while still land-grabbing. Set to minArmyToPush so an expansionist bot can COMMIT a wave
  // while it expands — below this the threat clock fired into a no-op (army stuck at 2 < 4) and the
  // "never stops spreading" presets read as passive for the whole multi-minute opening. Only the very
  // first colony still banks purely (armyFloor 0 in macroBuild) so the amino wall is crossed fast.
  earlyArmyFloor: 4,
  retreatHpFrac: 0.25,
};

// ============================================================================
// EVOLUTION TREE  (simple v1; the infra makes iteration trivial)
// GENOME = whole system; a purchase = a MUTATION; tier-up = SPECIATION; gates = KEYSTONE.
// ============================================================================
export type MutationType = 'somatic' | 'regulatory' | 'metabolic' | 'speciation';
// A boost's target: one combat class, the healer, or 'all' (every unit EXCEPT the Queen — she has
// her own QUEEN.* multipliers and is deliberately kept separate from the genome boosts).
export type EvoTarget = WarriorClass | 'healer' | 'all';
// Which sub-tab of the Evolve sheet a node lives under. Speciation + keystones are tier-defining and
// show regardless of the toggle; everything else declares 'unit' or 'economy'. Absent = 'unit'.
export type MutationCategory = 'unit' | 'economy';
export interface Mutation {
  id: string;
  name: string;
  type: MutationType;
  tier: number; // which tier band it belongs to
  category?: MutationCategory; // Evolve-sheet sub-tab (unit | economy); absent = 'unit'
  icon: string; // glyph (never color alone)
  blurb: string;
  cost: { energy: number; amino: number; toxin: number };
  maxLevel: number; // >1 for stackable somatics
  keystone?: boolean; // mutually exclusive within its tier band
  lineage?: string; // which lineage it belongs to / gates
  requires?: string[]; // prerequisite mutation ids
  // gates:
  gateTier?: number; // tier band must be unlocked
  gatePop?: number; // min population
  gateNodeType?: NodeType; // must hold a node of this type
  gateNodes?: number; // min capturable nodes held
  // effects (read by sim; simple multiplicative/flag model):
  effect?: MutationEffect;
}
export interface MutationEffect {
  unlockClass?: WarriorClass; // regulatory: unlock a warrior class
  // Combat somatics — class is a single unit, 'healer', or 'all' (all units except the Queen).
  // Each owned level multiplies the stat by `mult` (compounds via Math.pow(mult, level)).
  hpMult?: { class: EvoTarget; mult: number }; // retroactive: rescales LIVING units on purchase
  speedMult?: { class: EvoTarget; mult: number }; // live: read every tick in moveUnits
  dmgMult?: { class: EvoTarget; mult: number }; // read in applyHit + the toxifer DoT tick
  photoLevel?: number; // metabolic: enable/level photosynthesis
  toxinResist?: boolean; // metabolic: immunity + mining
  popPerNodeBonus?: number; // +cap per forward node
  popCeilingBonus?: number; // raises the hard POP.ceiling (Tier-IV Metazoan breaks the 40 cap)
  aerobic?: boolean; // Aerobic Respiration keystone (the oxygen bet)
  unlockTurrets?: boolean; // regulatory: unlock home defensive turrets (tap-placed structures)
  speciateToTier?: number; // speciation: advance tier
  // ---- Economy boosts (multipliers per owned level; compound via Math.pow) ----
  energyMult?: number; // >1 = faster energy income (e.g. 1.12 = +12%/level)
  aminoMult?: number; // >1 = faster amino income
  buildTimeMult?: number; // <1 = faster unit production (e.g. 0.9 = -10%/level)
  buildCostMult?: number; // <1 = cheaper units (scales energy+amino cost)
}

/** Speciation gates to ENTER each tier (checked before offering the speciation). */
export const SPECIATION_GATES = {
  2: { pop: 8, energy: 260, amino: 44, nodes: 1 },
  3: { pop: 16, energy: 620, amino: 94, nodes: 2 },
  4: { pop: 24, energy: 1250, amino: 165, nodes: 3 },
} as const;

// PHASE-1 SKELETON tree. Structure over balance — every `cost`/`mult` here is a placeholder to be
// tuned in microbe-sandbox later (Ara: "don't worry too much about equilibrium"). The Evolve sheet
// groups by `tier` (tabs) then `category` (Units | Economy); speciation + keystones show in any tab.
// Class availability by tier: T1 = lancer/phalanx/drone · T2 adds stinger/toxifer/engulfer · T3 healer.
export const MUTATIONS: Mutation[] = [
  // ================= TIER I  (base triangle: lancer / phalanx / drone) =================
  // -- Units --
  { id: 't1_thick_wall', name: 'Thick Cell Wall', type: 'somatic', tier: 1, category: 'unit', icon: '🛡', blurb: '+20% Phalanx HP.', cost: { energy: 60, amino: 6, toxin: 0 }, maxLevel: 3, effect: { hpMult: { class: 'phalanx', mult: 1.2 } } },
  { id: 't1_flagella', name: 'Flagella', type: 'somatic', tier: 1, category: 'unit', icon: '🌀', blurb: '+15% Lancer speed.', cost: { energy: 60, amino: 6, toxin: 0 }, maxLevel: 3, effect: { speedMult: { class: 'lancer', mult: 1.15 } } },
  { id: 't1_lancer_barb', name: 'Chitin Barb', type: 'somatic', tier: 1, category: 'unit', icon: '🗡', blurb: '+12% Lancer damage.', cost: { energy: 60, amino: 6, toxin: 0 }, maxLevel: 3, effect: { dmgMult: { class: 'lancer', mult: 1.12 } } },
  { id: 't1_phalanx_cilia', name: 'Marching Cilia', type: 'somatic', tier: 1, category: 'unit', icon: '🦶', blurb: '+12% Phalanx speed.', cost: { energy: 60, amino: 6, toxin: 0 }, maxLevel: 2, effect: { speedMult: { class: 'phalanx', mult: 1.12 } } },
  { id: 't1_scout_fin', name: 'Streamlined Fin', type: 'somatic', tier: 1, category: 'unit', icon: '💨', blurb: '+12% Drone speed.', cost: { energy: 50, amino: 5, toxin: 0 }, maxLevel: 3, effect: { speedMult: { class: 'drone', mult: 1.12 } } },
  { id: 't1_scout_nip', name: 'Nipping Cytostome', type: 'somatic', tier: 1, category: 'unit', icon: '🔪', blurb: '+10% Drone damage.', cost: { energy: 50, amino: 5, toxin: 0 }, maxLevel: 2, effect: { dmgMult: { class: 'drone', mult: 1.1 } } },
  // -- Economy --
  { id: 't1_energy_flux', name: 'Proton Pumps', type: 'metabolic', tier: 1, category: 'economy', icon: '⚡', blurb: '+12% energy income.', cost: { energy: 70, amino: 8, toxin: 0 }, maxLevel: 3, effect: { energyMult: 1.12 } },
  { id: 't1_amino_flux', name: 'Nitrogen Fixation', type: 'metabolic', tier: 1, category: 'economy', icon: '🧪', blurb: '+12% amino income.', cost: { energy: 70, amino: 8, toxin: 0 }, maxLevel: 3, effect: { aminoMult: 1.12 } },

  // ================= TIER II speciation (Eukaryote) =================
  { id: 'spec_t2', name: 'Speciate → Eukaryote', type: 'speciation', tier: 2, icon: '🧬', blurb: 'Advance to Tier II. Unlocks keystones + first specialist.', cost: { energy: 260, amino: 44, toxin: 0 }, maxLevel: 1, gatePop: 8, gateNodes: 1, effect: { speciateToTier: 2 } },

  // ---- Tier II keystones (mutually exclusive — pick your lineage/opening) ----
  { id: 'k_phototroph', name: 'Phototroph (Chloroplast)', type: 'metabolic', tier: 2, icon: '☀', blurb: 'Sunlight becomes the meta. Light Shelves + open water pay. Turtle the surface.', cost: { energy: 180, amino: 34, toxin: 0 }, maxLevel: 1, keystone: true, lineage: 'phototroph', gateTier: 2, requires: ['spec_t2'], effect: { photoLevel: 1 } },
  { id: 'k_chemotroph', name: 'Chemotroph (Chemosynthesis)', type: 'metabolic', tier: 2, icon: '☣', blurb: 'Immune to toxin; mine deep vents + seeps. The abyss becomes your fortress. Unlocks Toxifer.', cost: { energy: 180, amino: 34, toxin: 0 }, maxLevel: 1, keystone: true, lineage: 'chemotroph', gateTier: 2, gateNodeType: 'vent', requires: ['spec_t2'], effect: { toxinResist: true, unlockClass: 'toxifer' } },
  { id: 'k_phagotroph', name: 'Phagotroph (Predation)', type: 'regulatory', tier: 2, icon: '🦠', blurb: 'Energy from kills; +15% Lancer speed. Unlocks Engulfer. Aggro tempo.', cost: { energy: 180, amino: 34, toxin: 0 }, maxLevel: 1, keystone: true, lineage: 'phagotroph', gateTier: 2, gatePop: 8, requires: ['spec_t2'], effect: { unlockClass: 'engulfer', speedMult: { class: 'lancer', mult: 1.15 } } },

  // ---- Tier II units (specialists unlock this tier) ----
  { id: 't2_stinger_venom', name: 'Venom Gland', type: 'somatic', tier: 2, category: 'unit', icon: '🎯', blurb: '+15% Stinger damage.', cost: { energy: 90, amino: 12, toxin: 0 }, maxLevel: 3, gateTier: 2, effect: { dmgMult: { class: 'stinger', mult: 1.15 } } },
  { id: 't2_engulfer_wall', name: 'Reinforced Vacuole', type: 'somatic', tier: 2, category: 'unit', icon: '🫧', blurb: '+18% Engulfer HP.', cost: { energy: 110, amino: 14, toxin: 0 }, maxLevel: 2, gateTier: 2, effect: { hpMult: { class: 'engulfer', mult: 1.18 } } },
  { id: 't2_toxifer_conc', name: 'Concentrated Toxin', type: 'somatic', tier: 2, category: 'unit', icon: '☠', blurb: '+15% Toxifer damage (DoT).', cost: { energy: 90, amino: 12, toxin: 5 }, maxLevel: 3, gateTier: 2, effect: { dmgMult: { class: 'toxifer', mult: 1.15 } } },
  // ---- Tier II economy ----
  { id: 't2_broodplus', name: 'Prolific Brood', type: 'somatic', tier: 2, category: 'economy', icon: '🥚', blurb: '+2 pop cap per forward node.', cost: { energy: 110, amino: 12, toxin: 0 }, maxLevel: 2, gateTier: 2, effect: { popPerNodeBonus: 2 } },
  { id: 't2_fast_mitosis', name: 'Rapid Mitosis', type: 'regulatory', tier: 2, category: 'economy', icon: '⏩', blurb: '−10% unit build time.', cost: { energy: 120, amino: 16, toxin: 0 }, maxLevel: 2, gateTier: 2, effect: { buildTimeMult: 0.9 } },
  { id: 't2_turret', name: 'Sessile Fortress', type: 'regulatory', tier: 2, category: 'unit', icon: '🗼', blurb: 'Grow defensive turrets around your home. Tap to place. +1 slot per tier (1/2/3).', cost: { energy: 160, amino: 20, toxin: 0 }, maxLevel: 1, gateTier: 2, requires: ['spec_t2'], effect: { unlockTurrets: true } },

  // ================= TIER III speciation (Colonial) =================
  { id: 'spec_t3', name: 'Speciate → Colonial', type: 'speciation', tier: 3, icon: '🧬', blurb: 'Advance to Tier III. Lineage keystones + Aerobic Respiration.', cost: { energy: 620, amino: 94, toxin: 0 }, maxLevel: 1, gatePop: 16, gateNodes: 2, requires: ['spec_t2'], effect: { speciateToTier: 3 } },

  // ---- Tier III off-lineage keystone: the oxygen bet ----
  { id: 'k_aerobic', name: 'Aerobic Respiration', type: 'metabolic', tier: 3, icon: '🫁', blurb: 'THE OXYGEN BET. Immune to Great Oxygenation erosion. O2 becomes fuel (+energy scaling with map control).', cost: { energy: 320, amino: 56, toxin: 20 }, maxLevel: 1, keystone: true, gateTier: 3, gateNodeType: 'vent', requires: ['spec_t3'], effect: { aerobic: true } },

  // ---- Tier III units: army-wide (blanket) capstones + Healer ----
  { id: 't3_all_speed', name: 'Coordinated Ciliature', type: 'somatic', tier: 3, category: 'unit', icon: '🌊', blurb: '+10% speed to ALL units.', cost: { energy: 220, amino: 30, toxin: 0 }, maxLevel: 2, gateTier: 3, effect: { speedMult: { class: 'all', mult: 1.1 } } },
  { id: 't3_all_dmg', name: 'Universal Proteases', type: 'somatic', tier: 3, category: 'unit', icon: '⚔', blurb: '+10% damage to ALL units.', cost: { energy: 240, amino: 34, toxin: 0 }, maxLevel: 2, gateTier: 3, effect: { dmgMult: { class: 'all', mult: 1.1 } } },
  { id: 't3_all_hp', name: 'Peptidoglycan Mesh', type: 'somatic', tier: 3, category: 'unit', icon: '🧱', blurb: '+8% HP to ALL units.', cost: { energy: 240, amino: 34, toxin: 0 }, maxLevel: 2, gateTier: 3, effect: { hpMult: { class: 'all', mult: 1.08 } } },
  { id: 't3_healer_ferry', name: 'Ferrying Cilia', type: 'somatic', tier: 3, category: 'unit', icon: '➕', blurb: '+14% Healer speed.', cost: { energy: 160, amino: 22, toxin: 0 }, maxLevel: 2, gateTier: 3, effect: { speedMult: { class: 'healer', mult: 1.14 } } },
  // ---- Tier III economy ----
  { id: 't3_energy_bloom', name: 'Electron Cascade', type: 'metabolic', tier: 3, category: 'economy', icon: '🔋', blurb: '+18% energy income.', cost: { energy: 260, amino: 30, toxin: 0 }, maxLevel: 2, gateTier: 3, effect: { energyMult: 1.18 } },
  { id: 't3_thrift', name: 'Efficient Anabolism', type: 'regulatory', tier: 3, category: 'economy', icon: '💠', blurb: '−12% unit cost.', cost: { energy: 260, amino: 30, toxin: 0 }, maxLevel: 2, gateTier: 3, effect: { buildCostMult: 0.88 } },

  // ---- Tier III LINEAGE CHAINS (requires your Tier-II keystone — the tree finally diverges) ----
  // Phototroph → surface economy that compounds toward a Domination race.
  { id: 't3_grana', name: 'Grana Stacks', type: 'metabolic', tier: 3, category: 'economy', icon: '🍃', blurb: 'Photosynthesis Lv2. Light shelves compound.', cost: { energy: 240, amino: 28, toxin: 0 }, maxLevel: 1, gateTier: 3, requires: ['k_phototroph'], lineage: 'phototroph', effect: { photoLevel: 2 } },
  { id: 't3_radiant', name: 'Radiant Cascade', type: 'metabolic', tier: 3, category: 'economy', icon: '🌞', blurb: '+25% energy income (surface snowball).', cost: { energy: 280, amino: 32, toxin: 0 }, maxLevel: 2, gateTier: 3, requires: ['k_phototroph'], lineage: 'phototroph', effect: { energyMult: 1.25 } },
  // Chemotroph → abyssal poison fortress: deeper DoT + a tank shell to hold the floor.
  { id: 't3_venom_cascade', name: 'Venom Cascade', type: 'somatic', tier: 3, category: 'unit', icon: '🧬', blurb: '+30% Toxifer damage (zone the abyss).', cost: { energy: 240, amino: 30, toxin: 10 }, maxLevel: 2, gateTier: 3, requires: ['k_chemotroph'], lineage: 'chemotroph', effect: { dmgMult: { class: 'toxifer', mult: 1.3 } } },
  { id: 't3_chitin', name: 'Chitinous Plating', type: 'somatic', tier: 3, category: 'unit', icon: '🐚', blurb: '+12% HP to ALL units (hold the floor).', cost: { energy: 260, amino: 34, toxin: 10 }, maxLevel: 2, gateTier: 3, requires: ['k_chemotroph'], lineage: 'chemotroph', effect: { hpMult: { class: 'all', mult: 1.12 } } },
  // Phagotroph → predation rush: fatter bite + a faster army to end it before the oxygen clock.
  { id: 't3_frenzy', name: 'Devouring Frenzy', type: 'somatic', tier: 3, category: 'unit', icon: '😾', blurb: '+30% Engulfer damage.', cost: { energy: 240, amino: 30, toxin: 0 }, maxLevel: 2, gateTier: 3, requires: ['k_phagotroph'], lineage: 'phagotroph', effect: { dmgMult: { class: 'engulfer', mult: 1.3 } } },
  { id: 't3_swarm', name: 'Runaway Mitosis', type: 'regulatory', tier: 3, category: 'economy', icon: '🌀', blurb: '−18% unit build time (out-tempo).', cost: { energy: 260, amino: 30, toxin: 0 }, maxLevel: 2, gateTier: 3, requires: ['k_phagotroph'], lineage: 'phagotroph', effect: { buildTimeMult: 0.82 } },

  // ================= TIER IV speciation (Metazoan) — lights the long-dead tier-4 gate =================
  { id: 'spec_t4', name: 'Speciate → Metazoan', type: 'speciation', tier: 4, icon: '🧬', blurb: 'Advance to Tier IV. +15 pop ceiling + your lineage capstone.', cost: { energy: 1250, amino: 165, toxin: 0 }, maxLevel: 1, gatePop: 24, gateNodes: 3, requires: ['spec_t3'], effect: { speciateToTier: 4, popCeilingBonus: 15 } },

  // ---- Tier IV lineage capstones (requires spec_t4 + your lineage keystone; one fateful win-con each) ----
  { id: 'k_t4_photo', name: 'Oxygenic Bloom', type: 'metabolic', tier: 4, icon: '🌅', blurb: 'THE TIDE IS YOURS. +50% energy + O2 becomes fuel. Race Domination.', cost: { energy: 600, amino: 80, toxin: 0 }, maxLevel: 1, keystone: true, lineage: 'phototroph', gateTier: 4, requires: ['spec_t4', 'k_phototroph'], effect: { energyMult: 1.5, aerobic: true } },
  { id: 'k_t4_chemo', name: 'Abyssal Carapace', type: 'metabolic', tier: 4, icon: '🛡', blurb: 'THE ABYSS NEVER BREATHED. +20% HP to all + toxin immunity. Outlast.', cost: { energy: 600, amino: 80, toxin: 30 }, maxLevel: 1, keystone: true, lineage: 'chemotroph', gateTier: 4, requires: ['spec_t4', 'k_chemotroph'], effect: { hpMult: { class: 'all', mult: 1.2 }, toxinResist: true } },
  { id: 'k_t4_phago', name: 'Apex Predator', type: 'regulatory', tier: 4, icon: '🦈', blurb: 'END IT BEFORE THE OXYGEN COMES. +25% damage & +15% speed to all. Conquest.', cost: { energy: 600, amino: 80, toxin: 0 }, maxLevel: 1, keystone: true, lineage: 'phagotroph', gateTier: 4, requires: ['spec_t4', 'k_phagotroph'], effect: { dmgMult: { class: 'all', mult: 1.25 }, speedMult: { class: 'all', mult: 1.15 } } },
];

// ============================================================================
// CAMERA / INPUT (portrait, one-thumb)
// ============================================================================
export const CAMERA = {
  zoomMin: 0.233, // most zoomed OUT — was 0.35; lowered ~1.5x for a wider overview
  zoomMax: 2.2,
  zoomLerp: 2.5,
  camLerp: 6,
  tapMagnetRadiusPx: 44, // screen-space tap disc
  edgeMargin: 200, // pan clamp margin (world units)
  // Edge-scroll steering: hold + drag past the tap slop and the camera pulls TOWARD the finger,
  // speed scaling from 0 (near centre, inside the deadzone) up to edgeScrollSpeed at the screen edge.
  edgeScrollSpeed: 1300, // max pan speed in screen px/s when the finger is at the very edge
  edgeScrollDeadzone: 0.12, // fraction of the half-screen around centre where the camera holds still
};
/** Selection behavior (multi-select is one-thumb: cluster-grab + double-tap, no box-select in v1). */
export const SELECT = {
  softCap: 24, // max units in one grab (bible soft cap)
  doubleTapMs: 320, // repeat-tap window: 2nd tap -> all of kind, 3rd tap -> all in range
  // Double/triple selection radius as a fraction of the SMALLER viewport dimension — bounded to
  // roughly what you can see, never map-wide. (radius 0.30 -> ~60% of viewport across; 0.42 -> ~84%.)
  kindRadiusFrac: 0.3, // 2nd tap: same kind within ~60% of viewport
  rangeRadiusFrac: 0.42, // 3rd tap: all your units within ~84% of viewport
};

/** Tap-magnetism priority: higher wins within the 44px disc. */
export const TAP_PRIORITY = {
  enemyUnit: 100,
  enemyNode: 70,
  neutralNode: 50,
  ownUnit: 30,
  ownNode: 25,
  water: 0,
};

// haptic vocabulary
export const HAPTIC = {
  command: 8,
  select: 12,
  attack: [8, 20],
  loss: [20, 40],
  underAttack: [30, 50, 30],
} as const;

// per-band stance default
export const DEFAULT_BAND_STANCE: BandStance = 'aggressive';
